import React, {FC, ReactElement, useEffect, useMemo, useState} from 'react'
import {ApiProduct, ApiTaskDataResourceAsset} from 'sfportal_services_api/apiSchemas'
import {ListItemGroup} from '../../../components/ListItem/ListItemGroup'
import {apiGetFolderAssets} from '../../services/api/folderApiService'
import {ListItemTask} from '../../../components/ListItem/ListItemTask'
import {mdiDotsHorizontal, mdiDownload, mdiFileDocumentOutline, mdiImageOutline} from '@mdi/js'
import {setCurrentFile, useProductDetailStore} from '../../../stores/productDetailStore'
import {noop} from '../../../utils/function'
import {ButtonDropdown} from '../../../components/Forms/ButtonDropdown'
import {MenuItem} from '../../../components/Menu/MenuItem'
import {MdiIcon} from "../../../components/Generic/MdiIcon";
import {AssetTreeNode, AssetTreeNodeResponse} from "../ProductDetail/utils/ProductDetailViewUtils";
import {cstGetFileUriFromEMK} from '../../CstRoutes'
import {LoadingAnimation} from "../../../components/Generic/LoadingAnimation";
import './ListItemInformation.scss'

/**
 * Properties mit einem ApiProduct.
 */
interface Props {
    product: ApiProduct
}

/**
 * ListItems mit Autoreninformationen.
 *
 * @param product Das Produkt.
 * @constructor Function Konstruktor.
 */
export function ListItemInformation({
    product
}: Props): ReactElement {
    /**
     * Gibt an, ob die Auswahl offen ist.
     */
    const [isOpen, setIsOpen] = useState(false)

    /**
     * Die Assets der Autoreninformationen.
     */
    const [nodes, setNodes] = useState<AssetTreeNode[] | null>(null)

    /**
     * Wechselt den State des isOpen Proerpties und toggelt somit die Ordner.
     */
    const toggleOpen = (): void => {
        setIsOpen(!isOpen)
    }

    useEffect(() => {
        apiGetFolderAssets(product.id, 'authorinformation')
            .then((result) => {
                setNodes((result.body as AssetTreeNodeResponse).children)
            }).catch((error) => {
            console.log(error)
        })
    }, [product.id])

    return (<div className={'author-information'}>
            <ListItemGroup
                title={'Unterlagen'}
                isOpen={isOpen}
                onIsOpenChange={toggleOpen}>
                {
                    nodes?.map(node =>
                        <AssetListNode node={node} withoutLock={true} product={product} folderKey={'authorinformation'} maxDepth={2} />
                    )
                }
            </ListItemGroup>
        </div>
    )
}

interface AssetListNodeProps {
    node: AssetTreeNode
    withoutLock: boolean
    product: ApiProduct
    folderKey: string
    currentDepth?: number
    maxDepth: number
}

/**
 * Folder asset node component
 * @param {AssetTreeNode} node The external asset node
 * @param {boolean} withoutLock The locking status
 * @param {ApiProduct} product The product to load the assets for
 * @param {string} folderKey The folder key
 * @param {number} currentDepth The current depth for recursive rendering
 * @param {number} maxDepth The maximum depth to stop recursion
 */
const AssetListNode: FC<AssetListNodeProps> = ({
    node,
    withoutLock,
    product,
    folderKey,
    currentDepth = 0,
    maxDepth
}) => {

    const [open, setOpen] = useState<boolean>(false)

    const [children, setChildren] = useState<AssetTreeNode[]>([])

    const [childrenLoaded, setChildrenLoaded] = useState<boolean>(false)

    const [loading, setLoading] = useState<boolean>(false)

    /**
     * Loads the children for the node
     */
    const loadChildren = async (): Promise<void> => {
        if (childrenLoaded) {
            return
        }
        setLoading(true)
        const result = (await apiGetFolderAssets(product.id, folderKey, node.id)).body as AssetTreeNodeResponse
        setChildren(result.children)
        setLoading(false)
        setChildrenLoaded(true)
    }

    /**
     * Toggles the open state
     */
    const toggleOpen = (): void => {
        if (currentDepth < maxDepth) {
            loadChildren()
            setOpen(prevState => !prevState)
        }
    }

    if (node.type == 'asset' && node.asset) {
        return <ListItemInformationAsset asset={node.asset} withoutLock={withoutLock} />
    }

    return <ListItemGroup title={node.identifier} isOpen={open} onIsOpenChange={toggleOpen}>
        {children.map(child => {
            if (currentDepth+1 >= maxDepth && child.type == 'folder') {
                return null;
            }
            return <AssetListNode
                node={child}
                withoutLock={withoutLock}
                product={product}
                folderKey={folderKey}
                currentDepth={currentDepth + 1}
                maxDepth={maxDepth} />
        })}
        {loading && <LoadingAnimation />}
    </ListItemGroup>
}

/**
 * Properties eines einzelnen InformationAssets.
 */
interface InformationAssetProps {
    asset: ApiTaskDataResourceAsset
    withoutLock?: boolean
}

function ListItemInformationAsset({
    asset,
    withoutLock
}: InformationAssetProps): ReactElement {
    const {currentFile} = useProductDetailStore()

    /**
     * Öffnet die Preview im Preview Container des Portals.
     */
    const handleItemClick = (): void => {
        setCurrentFile(asset).catch(noop)
    }

    /**
     * Der Downloadlink des Assets.
     */
    const downloadLink = useMemo(
        () => cstGetFileUriFromEMK(asset.id, 'download', withoutLock),
        [asset.id, withoutLock]
    )

    return (<ListItemTask
            listItemType="asset"
            title={asset.name}
            icon={<MdiIcon path={asset.contentType?.startsWith('image') ?? false
                ? mdiImageOutline : mdiFileDocumentOutline} />}
            onItemClick={handleItemClick}
            current={currentFile?.id === asset.id}
            modifiedBy={asset.modifiedBy ?? 'unbekannt'}
            modifiedDate={asset.modifiedDate ?? 'unbekannt'}
            actions={<>
                {downloadLink !== null ? <ButtonDropdown
                    buttonStyle="inline"
                    buttonIcon={mdiDotsHorizontal}
                    title="Weitere Aktionen">
                    <MenuItem
                        label="Datei herunterladen"
                        icon={mdiDownload}
                        action={downloadLink}
                    />
                </ButtonDropdown> : null}
            </>}
        />
    )
}
